import { TemplatePortal } from '@angular/cdk/portal';
import {
  afterNextRender,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@cmi/addin-core';
import { LoadingSpinner, Notification } from '@cmi/ui';
import { filter } from 'rxjs';
import { App } from '../app';
import { FeatureToggleService } from '../shared/feature-toggle.service';
import { SearchBar } from '../shared/search-bar/search-bar';
import { ServerVersionService } from '../shared/server-version.service';
import { HomeService } from './home.service';
import { SearchResults } from './search-results/search-results';

@Component({
  selector: 'cmi-home',
  imports: [TranslatePipe, SearchBar, RouterModule, LoadingSpinner, Notification, SearchResults],
  templateUrl: './home.html',
  host: { class: 'flex flex-col h-full' }
})
export class Home implements OnDestroy {
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _appComponent = inject(App);
  private readonly _router = inject(Router);

  private readonly _footerPortal = viewChild.required<TemplateRef<unknown>>('footerPortal');
  private readonly _homeService = inject(HomeService);
  private readonly _serverVersionService = inject(ServerVersionService);
  private readonly _featureToggleService = inject(FeatureToggleService);

  private readonly _navigationEnd = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    )
  );

  protected readonly tags = this._homeService.appDetails;
  protected readonly reports = this._homeService.reportsAppDetails;
  protected readonly supportsReports = this._featureToggleService.supportsReports;

  protected readonly isLoading = computed(
    () =>
      this._homeService.rootTypeLoading() ||
      this._homeService.appDetailsLoading() ||
      this._serverVersionService.isLoading()
  );

  protected readonly hasError = computed(
    () => !!(this._homeService.rootTypeError() || this._homeService.appDetailsError())
  );

  protected readonly searchTerm = signal<string>('');

  public constructor() {
    afterNextRender(() => {
      const portal = new TemplatePortal(this._footerPortal(), this._viewContainerRef);
      this._appComponent.footerPortal.set(portal);
    });

    // Reset search term when navigating to home page
    effect(() => {
      const navEvent = this._navigationEnd();
      if (
        navEvent &&
        (navEvent.urlAfterRedirects === '/apps' || navEvent.urlAfterRedirects === '/')
      ) {
        this.searchTerm.set('');
      }
    });
  }

  public ngOnDestroy(): void {
    this._appComponent.footerPortal.set(null);
  }

  public onSearchTermChanged(term: string): void {
    this.searchTerm.set(term);
  }

  public onCloseResults(): void {
    this.searchTerm.set('');
  }

  public onReload(): void {
    this._homeService.reload();
  }
}
